import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import PropTypes from 'prop-types';

import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import useLoanId from 'hooks/useLoanId';
import useReleaseMyFieldLocks from 'hooks/useReleaseMyFieldLocks';
import useRevertFieldValues from 'hooks/useRevertFieldValues';
import { calculatePendingChanges } from 'utils/pendingChanges';

import {
  selectEntityChangedPropsById,
  selectIsPostClosingLoaded,
  selectIsUnsecuredFromPropertyAndLoanSummary,
} from 'store/loanApplications/selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';

import Content from './Content';
import {
  calculateSectionPendingChangesCounts,
  DEFAULT_SECTION,
  getNavigationTabs,
} from './EditDrawer.constants';
import { getVisibleSectionHrefs } from './EditDrawer.utils';

const EditDrawer = ({ isLoadFailed = false, onRetry }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { applicationId, tab, subtab: section } = useParams();
  const loanId = useLoanId();
  const revertFieldValues = useRevertFieldValues();
  const releaseMyFieldLocks = useReleaseMyFieldLocks();
  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);

  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isPostClosingLoaded = useSelector(selectIsPostClosingLoaded);

  const isOpen = tab === LoanApplicationTabRouteParam.POST_CLOSING && !!section;
  const isPostClosingLoadFailed = isLoadFailed && !isPostClosingLoaded;
  const currentSection = section || DEFAULT_SECTION;

  const isValidSection = getVisibleSectionHrefs(isUnsecured).includes(currentSection);

  const [, setPendingChanges] = useState({});
  const [formPendingChangesCounts, setFormPendingChangesCounts] = useState({});

  const backendPendingChangesCounts = useMemo(
    () => calculateSectionPendingChangesCounts(pendingChangesData),
    [pendingChangesData],
  );

  const pendingChangesCounts = useMemo(() => {
    const merged = { ...backendPendingChangesCounts };
    Object.entries(formPendingChangesCounts).forEach(([sectionKey, count]) => {
      merged[sectionKey] = Math.max(merged[sectionKey] || 0, count);
    });

    return merged;
  }, [backendPendingChangesCounts, formPendingChangesCounts]);

  const onCountPendingChanges = useCallback((changes, sectionKey) => {
    const { count } = calculatePendingChanges(changes);
    setFormPendingChangesCounts(prev => ({
      ...prev,
      [sectionKey]: count,
    }));
  }, []);

  const onClose = useCallback(() => {
    if (localEditedFieldPaths.length) {
      releaseMyFieldLocks();
      revertFieldValues(localEditedFieldPaths);
      dispatch(clearLocalEditedFieldsAction());
    }

    const path = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
    });
    setPendingChanges({});
    setFormPendingChangesCounts({});
    history.push(path);
  }, [
    applicationId,
    tab,
    history,
    localEditedFieldPaths,
    revertFieldValues,
    releaseMyFieldLocks,
    dispatch,
  ]);

  const additionalProps = useMemo(
    () => ({
      onCountPendingChanges,
      setPendingChanges,
      isPostClosingLoadFailed,
      onRetry,
    }),
    [onCountPendingChanges, isPostClosingLoadFailed, onRetry],
  );

  const navigationTabs = useMemo(
    () => getNavigationTabs({ applicationId, tab, history, pendingChangesCounts, isUnsecured }),
    [applicationId, tab, history, pendingChangesCounts, isUnsecured],
  );

  if (!isOpen || !isValidSection) {
    return null;
  }

  return (
    <EntityFormModal
      isData
      Content={Content}
      additionalProps={additionalProps}
      isLoading={!isPostClosingLoaded && !isPostClosingLoadFailed}
      isOpen={isOpen}
      navigationTabs={navigationTabs}
      navigationValue={currentSection}
      title="Post Closing"
      onClose={onClose}
    />
  );
};

EditDrawer.propTypes = {
  onRetry: PropTypes.func.isRequired,
  isLoadFailed: PropTypes.bool,
};

export default memo(EditDrawer);
